import { Box, Flex } from '@chakra-ui/react'
import { Body1, H3 } from 'components/typography'
import { FC, Fragment } from 'react'

import { OrganicLink } from '../../../medleveSeo/components/OrganicLink'
import { getBrandsByMolecule, MOLECULES } from '../../../medleveSeo/registry'
import { HOME_MEDICATIONS } from '../content'
import HomeSection from './HomeSection'

/** "A", "A e B", "A, B e C". */
const separator = (index: number, total: number): string => {
  if (index === 0) return ''
  return index === total - 1 ? ' e ' : ', '
}

/**
 * PRD §17 — "Medicamentos e moléculas frequentemente pesquisados".
 *
 * Not a keyword list: each molecule is shown with the brands that use it, read from the medleveSeo
 * registry (PRD §30/§31/§48 — the same relations that feed JSON-LD `mentions`).
 * Every name goes through `OrganicLink`, which renders PLAIN TEXT while the target page is unpublished
 * (PRD §85) and becomes a contextual link automatically once its registry entry is published — nothing
 * here can produce a broken URL.
 * REQUIRES MEDICAL REVIEW · REQUIRES LEGAL REVIEW — copy in content.ts (`HOME_MEDICATIONS`).
 */
const HomeMedications: FC = () => (
  <HomeSection id="medicamentos" title={HOME_MEDICATIONS.title} paragraphs={[HOME_MEDICATIONS.intro]}>
    <Flex as="ul" listStyleType="none" m={0} p={0} direction="column" gap={4}>
      {MOLECULES.map((molecule) => {
        const brands = getBrandsByMolecule(molecule.slug)
        if (!brands.length) return null

        return (
          <Flex
            as="li"
            key={molecule.slug}
            bg="white"
            borderRadius={{ base: '24px', lg: '32px' }}
            direction="column"
            p={6}
            gap={2}
          >
            <H3
              fontWeight={800}
              fontSize={{ base: '1rem', lg: '1.5rem' }}
              letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
              lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
            >
              <OrganicLink path={molecule.path}>{molecule.name}</OrganicLink>
            </H3>
            <Body1>
              {brands.length === 1 ? HOME_MEDICATIONS.groupLabelSingular : HOME_MEDICATIONS.groupLabelPlural}{' '}
              {molecule.name.toLowerCase()}:{' '}
              {brands.map((brand, index) => (
                <Fragment key={brand.slug}>
                  {separator(index, brands.length)}
                  <OrganicLink path={brand.path}>{brand.name}</OrganicLink>
                </Fragment>
              ))}
              .
            </Body1>
          </Flex>
        )
      })}
    </Flex>
    <Box>
      <Body1>{HOME_MEDICATIONS.clinicalNote}</Body1>
    </Box>
    <Box>
      <Body1>{HOME_MEDICATIONS.pharmacyNote}</Body1>
    </Box>
  </HomeSection>
)

export default HomeMedications
